// Data access for the founders' SLA Support dashboard. Shapes mirror
// apps/api/src/support-model.ts; requests come from the Global DB.
import { parseTimestamp } from "@pgs-core/portal-lib/format";
import type { ApiClient } from "./api";

export type SupportRequestStatus = "open" | "in_progress" | "done";

export interface AdminSupportRequest {
  id: string;
  tenant_id: string;
  business_name: string | null;
  description: string;
  image_key: string | null;
  status: SupportRequestStatus;
  created_at: string;
  completed_at: string | null;
}

/** One SLA client's used-this-month quota. */
export interface SlaClientQuota {
  tenant_id: string;
  business_name: string | null;
  used: number;
  limit: number;
}

export interface AdminSupportResult {
  requests: AdminSupportRequest[];
  clients: SlaClientQuota[];
}

export async function fetchSupportRequests(
  api: ApiClient,
  status: "active" | "done"
): Promise<AdminSupportResult> {
  const query = status === "done" ? "?status=done" : "";
  const data = await api.request<{ requests?: AdminSupportRequest[]; clients?: SlaClientQuota[] }>(
    `/admin/support/requests${query}`
  );
  return { requests: data.requests ?? [], clients: data.clients ?? [] };
}

export async function updateSupportRequest(
  api: ApiClient,
  id: string,
  status: "in_progress" | "done"
): Promise<AdminSupportRequest> {
  const data = await api.request<{ request: AdminSupportRequest }>(
    `/admin/support/requests/${id}`,
    { method: "PATCH", body: JSON.stringify({ status }) }
  );
  return data.request;
}

/** Whole hours since the request was raised; null when unparseable. */
export function hoursOpen(request: AdminSupportRequest, now = Date.now()): number | null {
  const created = parseTimestamp(request.created_at);
  if (!created) return null;
  return Math.floor((now - created.getTime()) / 3_600_000);
}

/** The 24-hour turnaround promised by the SLA module. */
export const SLA_TURNAROUND_HOURS = 24;

/** Red-flag threshold: three quarters of the window gone = approaching breach. */
export const SLA_WARNING_HOURS = 18;
